import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, QrCode, Camera, Coins, Gift, CheckCircle, User } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "@/lib/api";
import { pts, initials, fmtDate, tzs, tierColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ScanFlow() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intent = params.get("intent");

  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState("");
  const [itemName, setItemName] = useState("");
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const tokenRef = useRef("");

  const err = (e) => toast.error(apiError(e.response?.data?.detail) || e.message);

  const doScan = async (token) => {
    if (!token) return toast.error("Enter or scan a QR token");
    tokenRef.current = token.trim();
    setBusy(true);
    try {
      const { data } = await api.post("/staff/scan", { qr_token: token.trim() });
      setCustomer(data);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const startCamera = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const el = document.getElementById("qr-reader");
        if (!el) return;
        const scanner = new Html5Qrcode("qr-reader");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          async (decoded) => { await stopCamera(); doScan(decoded); },
          () => {}
        );
      } catch (e) {
        toast.error("Camera unavailable — paste the token instead");
        setScanning(false);
      }
    }, 100);
  };

  const stopCamera = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  const method = (customer?.rule?.method || "SPEND").toUpperCase();

  const award = async () => {
    const payload = { onekite_id: customer.onekite_id };
    if (method === "SPEND") {
      if (!amount || Number(amount) <= 0) return toast.error("Enter a purchase amount");
      payload.purchase_amount = Number(amount);
    } else if (method === "QUANTITY") {
      if (!quantity || Number(quantity) <= 0) return toast.error("Enter a quantity");
      payload.quantity = Number(quantity);
      payload.purchase_amount = Number(amount) || 0;
    } else if (method === "ITEM") {
      if (!itemName) return toast.error("Select an item");
      payload.item_name = itemName;
      payload.purchase_amount = Number(amount) || 0;
    } else {
      payload.purchase_amount = Number(amount) || 0;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/staff/award", payload);
      if (data.blocked) {
        toast.error(`Blocked for review: ${data.reason}`);
      } else {
        toast.success(`+${data.points_awarded} points awarded`);
        if (data.risk) toast.warning(`Fraud risk: ${data.risk}`);
      }
      await doScan(tokenRef.current);
      setAmount(""); setQuantity(""); setItemName("");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const redeem = async (reward) => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/redeem", { onekite_id: customer.onekite_id, reward_id: reward.id });
      toast.success(`Redeemed: ${data.reward}`);
      await doScan(tokenRef.current);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const handleScanSubmit = (token) => doScan(token);

  if (customer) {
    return (
      <div className="space-y-5">
        <button onClick={() => setCustomer(null)} data-testid="scan-back" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Scan another</button>

        <div className="card-surface p-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center overflow-hidden">
              {customer.photo ? <img src={customer.photo} alt="" className="h-full w-full object-cover" /> : <span className="font-display font-bold text-primary text-xl">{initials(customer.name)}</span>}
            </div>
            <div>
              <h1 className="font-display font-bold text-xl" data-testid="scan-customer-name">{customer.name}</h1>
              <p className={`text-sm font-medium ${tierColor(customer.tier)}`}>{customer.tier} tier {customer.is_new && <span className="text-primary">· New</span>}</p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 text-center border-t border-black/10 pt-4">
            <div><p className="font-display font-bold text-2xl text-primary" data-testid="scan-points">{pts(customer.points)}</p><p className="text-[10px] text-muted-foreground">Points here</p></div>
            <div><p className="font-display font-bold text-2xl">{customer.visits}</p><p className="text-[10px] text-muted-foreground">Visits</p></div>
            <div><p className="font-display font-bold text-sm mt-2">{customer.last_visit ? fmtDate(customer.last_visit) : "—"}</p><p className="text-[10px] text-muted-foreground">Last visit</p></div>
          </div>
        </div>

        {intent !== "redeem" && (
          <div className="card-surface p-5">
            <p className="font-display font-semibold flex items-center gap-2 mb-3"><Coins size={18} className="text-primary" /> Award points</p>

            {method === "SPEND" && (
              <>
                <Input data-testid="award-amount" value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="Purchase amount (TZS)" className="bg-muted/40 border-black/10 h-12 rounded-xl text-lg" />
                {amount > 0 && <p className="text-xs text-muted-foreground mt-2">{tzs(amount)} purchase</p>}
              </>
            )}

            {method === "QUANTITY" && (
              <Input data-testid="award-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" placeholder={`Quantity (${customer.rule?.quantity_unit || "unit"})`} className="bg-muted/40 border-black/10 h-12 rounded-xl text-lg" />
            )}

            {method === "ITEM" && (
              <div className="space-y-2">
                {(customer.rule?.items || []).length === 0
                  ? <p className="text-sm text-muted-foreground">No items configured for this business.</p>
                  : (customer.rule.items || []).map((it) => (
                      <button key={it.name} data-testid={`award-item-${it.name}`} onClick={() => setItemName(it.name)}
                        className={`w-full flex items-center justify-between rounded-xl border p-3 text-left ${itemName === it.name ? "border-primary bg-primary/10" : "border-black/10"}`}>
                        <span className="font-medium text-sm">{it.name}</span>
                        <span className="text-xs text-muted-foreground">{it.points} pts</span>
                      </button>
                    ))}
              </div>
            )}

            {(method === "VISIT" || method === "TRANSACTION") && (
              <p className="text-sm text-muted-foreground" data-testid="award-fixed-note">
                {method === "VISIT" ? "Records a visit and awards" : "Awards a fixed"} {customer.rule?.points_value ?? ""} point(s).
              </p>
            )}

            <Button data-testid="award-confirm" onClick={award} disabled={busy} className="w-full h-12 mt-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              <CheckCircle size={18} weight="fill" className="mr-2" /> Confirm & award
            </Button>
          </div>
        )}

        <div className="card-surface p-5">
          <p className="font-display font-semibold flex items-center gap-2 mb-3"><Gift size={18} className="text-primary" /> Rewards</p>
          <div className="space-y-2">
            {customer.rewards.length === 0 && <p className="text-sm text-muted-foreground">No rewards configured.</p>}
            {customer.rewards.map((r) => (
              <div key={r.id} data-testid="scan-reward" className={`flex items-center justify-between rounded-xl border p-3 ${r.eligible ? "border-primary/40 bg-primary/5" : "border-black/10"}`}>
                <div>
                  <p className="font-medium text-sm">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{pts(r.points_required)} pts</p>
                </div>
                <Button data-testid="redeem-confirm" size="sm" disabled={!r.eligible || busy} onClick={() => redeem(r)}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                  Redeem
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/staff")} data-testid="scan-home" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Home</button>
      <div>
        <h1 className="font-display font-bold text-2xl flex items-center gap-2"><QrCode size={24} className="text-primary" /> Scan customer</h1>
        <p className="text-sm text-muted-foreground">Scan the customer&rsquo;s OneKite QR or paste their token.</p>
      </div>

      {scanning ? (
        <div className="card-surface p-4">
          <div id="qr-reader" className="rounded-xl overflow-hidden" />
          <Button variant="outline" onClick={stopCamera} className="w-full mt-3 rounded-xl border-black/10">Stop camera</Button>
        </div>
      ) : (
        <Button data-testid="start-camera" onClick={startCamera} className="w-full h-14 rounded-2xl bg-primary/15 text-primary hover:bg-primary/20 font-semibold border border-primary/20">
          <Camera size={22} className="mr-2" /> Open camera scanner
        </Button>
      )}

      <div className="card-surface p-5 space-y-3">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-mono">Or paste token</p>
        <Input data-testid="manual-token" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Paste QR token…" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono text-xs" />
        <Button data-testid="manual-scan-btn" onClick={() => handleScanSubmit(manual)} disabled={busy} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
          <User size={18} className="mr-2" /> {busy ? "Resolving…" : "Resolve customer"}
        </Button>
      </div>
    </div>
  );
}
